import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";

const BASE = "/admin/campus/";

/* Fase 2 — vender (2026-10-01): Nueva Venta, Metas y Bonos, Maestros. */

export interface SaleStudent { id: number; name: string; identityCode: string | null; phone: string | null; statusId: number; courses: string[] }

export interface SaleProduct {
	id: number; name: string; waCode: string | null; sku: string | null; kind: string | null; kindLabel: string; legacy: boolean; isKit: boolean;
	price: number; pricePending: boolean; hasPhoto: boolean; tracksStock: boolean;
	onHandHere: number | null; committedHere: number | null; availableHere: number | null; onHandCountry: number | null; availableCountry: number | null;
	elsewhere: { warehouseId: number; name: string; code: string; onHand: number }[]; inTransit: number; eta: string | null; homeWarehouse: string | null;
	sizes: string[]; required?: boolean;
	/** Tallas/colores del producto suelto, con su precio (puede ser propio) y existencia aquí. */
	variants?: SaleVariant[];
	/** Kit o uniforme: piezas con talla/color para elegir al vender (también se puede elegir al entregar). */
	kitChoices?: { componentId: number; modelId: number; name: string; quantity: number; optional: boolean; variants: SaleVariant[] }[];
	/** Solo en el lector de código: la talla del código escaneado (cuando el código es de una talla y no del modelo). */
	scannedSize?: string | null; scannedVariantId?: number | null; courseId?: number; alreadyBought?: boolean; alreadyCharged?: boolean;
}

export interface SaleVariant { id: number; label: string; colorValue: string | null; sizeValue: string | null; price: number; ownPrice: boolean; pricePending: boolean; onHand: number; waCode: string | null; barcode: string | null }

export interface SaleSeller { id: string; name: string; email: string | null; roles: string[]; kind: "team" | "teacher"; wappUserId?: number; isStudentTeacher?: boolean }

export interface SaleContext {
	student: { id: number; name: string; identityCode: string | null; phone: string | null; email: string | null; statusId: number };
	enrollments: { id: number; statusId: number; status: string; courseId: number; course: string; module: string | null; levelNumber: number | null; period: string | null;
		scheduleId: number; scheduleCode: string | null; mentorId: number | null; mentor: string | null; days: string; hours: string | null }[];
	proposed: SaleProduct[];
	bought: { id: number; articleId: number; name: string; statusId: number; status: string | null; total: number; paid: number; deliveryStatusId: number; date: string }[];
	sellers: SaleSeller[];
}

export class SalesService {
	searchStudents = async (campusId: number, q: string): Promise<AxiosResponse<SaleStudent[]>> =>
		axiosInstance.get(`${BASE}${campusId}/newSale/_students`, { params: { q } });
	studentContext = async (campusId: number, studentId: number): Promise<AxiosResponse<SaleContext>> =>
		axiosInstance.get(`${BASE}${campusId}/newSale/_student/${studentId}`);
	searchProducts = async (campusId: number, q: string, exact = false): Promise<AxiosResponse<SaleProduct[]>> =>
		axiosInstance.get(`${BASE}${campusId}/newSale/_products`, { params: { q, exact: exact ? 1 : 0 } });
	charge = async (campusId: number, data: { studentId: number; lines: { articleId: number; quantity: number; size?: string | null; variantId?: number | null; choices?: { modelId: number; variantId: number }[] }[]; advisors: { userId: string }[]; note?: string }): Promise<AxiosResponse<{ kitIds: number[]; total: number; pending: number }>> =>
		axiosInstance.post(`${BASE}${campusId}/newSale/_charge`, data);

	goals = async (campusId: number, year: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_goals`, { params: { year } });
	saveGoal = async (campusId: number, data: Record<string, any>): Promise<AxiosResponse<any[]>> =>
		axiosInstance.put(`${BASE}${campusId}/salesGoals/_goals`, data);
	goalHistory = async (campusId: number, targetCampusId: number, month: string): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_goalHistory`, { params: { targetCampusId, month } });
	rules = async (campusId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_rules`);
	saveRule = async (campusId: number, ruleId: number | null, data: Record<string, any>): Promise<AxiosResponse<any[]>> =>
		ruleId ? axiosInstance.put(`${BASE}${campusId}/salesGoals/_rules/${ruleId}`, data) : axiosInstance.post(`${BASE}${campusId}/salesGoals/_rules`, data);
	deactivateRule = async (campusId: number, ruleId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.delete(`${BASE}${campusId}/salesGoals/_rules/${ruleId}`);
	month = async (campusId: number, month: string): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_month`, { params: { month } });
	detail = async (campusId: number, month: string, person?: string): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_detail`, { params: { month, person } });
	ranking = async (campusId: number, month: string): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_ranking`, { params: { month } });
	bonusExcel = async (campusId: number, month: string): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/salesGoals/_excel`, { params: { month }, responseType: "blob" });
	approve = async (campusId: number, month: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/salesGoals/_approve`, { month });
	reopen = async (campusId: number, month: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/salesGoals/_reopen`, { month });
	markPaid = async (campusId: number, month: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/salesGoals/_paid`, { month });

	teacherBoard = async (campusId: number, month: string): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/teacherBoard`, { params: { month } });
	teacherSchedules = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/teacherBoard/_schedules`);
	teacherExcel = async (campusId: number, month: string): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/teacherBoard/_excel`, { params: { month }, responseType: "blob" });

	staff = async (campusId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_staff`);
	saveStaff = async (campusId: number, staffId: number | null, data: Record<string, any>): Promise<AxiosResponse<any>> =>
		staffId ? axiosInstance.put(`${BASE}${campusId}/productMargins/_staff/${staffId}`, data) : axiosInstance.post(`${BASE}${campusId}/productMargins/_staff`, data);
	deleteStaff = async (campusId: number, staffId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.delete(`${BASE}${campusId}/productMargins/_staff/${staffId}`);
}
